import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredComics = comics.filter((c) => c.category === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-4xl text-foreground-400 mb-4 block" />
            <h1 className="text-xl font-bold text-foreground-950 mb-2">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'CollectionPage',
              name: `${category.name} - 韩漫网`,
              url: `https://www.manhwa-read.com/category/${category.slug}`,
              description: `韩漫网${category.name}分类，收录丰富韩国漫画资源，免费在线阅读。`,
              inLanguage: 'zh-CN',
              isPartOf: { '@id': 'https://www.manhwa-read.com/#website' },
              mainEntity: {
                '@type': 'ItemList',
                itemListElement: filteredComics.map((comic, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    '@type': 'ComicSeries',
                    name: comic.title,
                    url: `https://www.manhwa-read.com/comic/${comic.slug}`,
                  },
                })),
              },
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.manhwa-read.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category.name,
                },
              ],
            },
          ],
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <section className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-[oklch(var(--accent-100))]">
                <i className={`${category.icon} text-2xl text-[oklch(var(--accent-500))]`} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  共 {filteredComics.length} 部漫画
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-[oklch(var(--primary-500))] text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`} />
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-book-2-line text-4xl text-foreground-400 mb-4 block" />
                <p className="text-foreground-600">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTopButton />
    </>
  );
}